import { Grid, Card, StatsCard } from "../index";
import CountUp from "react-countup";
import Chart from "react-apexcharts";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useInstance } from "../../hooks";

function AdminOperationalDashboard({ operational, revenue }) {
  const instance = useInstance();

  const barOptions = {
    chart: {
      type: "bar",
      // "Yellix" is not bundled or served, so the chart fell back to a generic
      // sans while the rest of the page rendered in Inter.
      fontFamily: "Inter, sans-serif",
      background: "transparent",
      toolbar: { show: false },
    },
    theme: { mode: "dark" },
    grid: {
      borderColor: "rgba(255, 255, 255, 0.08)",
      strokeDashArray: 4,
    },
    plotOptions: {
      bar: {
        columnWidth: "45%",
        borderRadius: 6,
        borderRadiusApplication: "end",
      },
    },
    colors: instance === "starlink" ? ["#fff"] : ["#C7FF00"],
    dataLabels: {
      enabled: false,
    },
    // Subscriptions are a count, but the axis was left to auto-scale and
    // produced fractional ticks -- "0.7" and "1.3" subscriptions.
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (value) => Math.round(value),
        style: { colors: "#8A9199" },
      },
    },
    xaxis: {
      labels: { style: { colors: "#8A9199" } },
      axisBorder: { color: "rgba(255, 255, 255, 0.08)" },
      axisTicks: { color: "rgba(255, 255, 255, 0.08)" },
    },
    tooltip: {
      theme: "dark",
      y: { formatter: (value) => `${Math.round(value)} subscriptions` },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      customLegendItems: ["Subscriptions"],
      labels: { colors: "#8A9199" },
      markers: {
        fillColors: instance === "starlink" ? ["#fff"] : ["#C7FF00"],
      },
    },
  };

  const data = useMemo(() => {
    return revenue?.subscriptions?.reduce((acc, val) => {
      const date = new Date(val.date);
      // getMonth() is 0-based, so August was labelled "2026-7". Show the month
      // by name rather than a number -- "2026-8" reads as a date fragment.
      const label = date.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });

      const index = acc.findIndex((item) => item.x === label);

      if (index === -1) {
        acc.push({ x: label, y: val.count });
      } else {
        acc[index].y += val.count;
      }

      return acc;
    }, []);
  }, [revenue?.subscriptions]);

  const barData = [
    {
      name: "Subscriptions",
      data: data,
    },
  ];

  return (
    <>
      <Grid columns={2}>
        <Grid columns={2}>
          <StatsCard
            title="Total Revenue"
            hasDetails={false}
            decimals={2}
            value={operational?.total_revenue}
          />
          <StatsCard
            title="Total Customers"
            hasDetails={false}
            decimals={0}
            value={operational?.total_customers}
          />
          <StatsCard
            title="Total Clients"
            decimals={0}
            value={operational?.total_clients}
            hasDetails={false}
          />
          <StatsCard
            title="Total Redemptions"
            hasDetails={false}
            decimals={0}
            value={operational?.total_redemptions}
          />
          <StatsCard
            title="Total Brands"
            hasDetails={false}
            decimals={0}
            value={operational?.total_brands}
          />
          <StatsCard
            title="Total Vouchers"
            hasDetails={false}
            decimals={0}
            value={operational?.total_vouchers}
          />
          <StatsCard
            title="Total Merchants"
            hasDetails={false}
            decimals={0}
            value={operational?.total_merchants}
          />
          <StatsCard
            title="Total Subscription Plan"
            hasDetails={false}
            decimals={0}
            value={operational?.toal_subscription_plans}
          />
        </Grid>

        <Card
          title="Total Subscriptions"
          subtitle={
            <span
              style={{
                fontSize: "30px",
                fontWeight: "normal",
              }}
            >
              <CountUp
                className="animate_number_decima"
                end={revenue.total_subscriptions}
              />
            </span>
          }
          className="height_full"
        >
          <ChartWrapper>
            <Chart
              options={barOptions}
              series={barData}
              type="bar"
              width="100%"
            />
          </ChartWrapper>
        </Card>
      </Grid>
    </>
  );
}

export const ChartWrapper = styled.div`
  padding: 24px 0;
  padding-right: ${(props) => props.theme.space[4]}px;
`;

export default AdminOperationalDashboard;
